<template>
  <TypeformContainer
    :estimatedCompletionTime="store.applicationForm.estimatedCompletionTime"
    :primaryForeground="store.applicationForm.primaryForeground"
    :primaryBackground="store.applicationForm.primaryBackground"
    :secondaryForeground="store.applicationForm.secondaryForeground"
    :secondaryBackground="store.applicationForm.secondaryBackground"
    :current-step="currentStepIndex"
    :total-steps="steps.length"
    :name="store.applicationForm.companyName"
    :avatar="store.applicationForm.companyAvatar"
    :logo="store.applicationForm.companyLogo"
    :initial-email="store.newUserDetails.email"
    :loading="store.applicationForm.loading"
    v-model:showLogIn="showLogIn"
    @update:showLoginIn="handleLogInUpdate"
    @logged-in="handleLoggedIn"
    coerce-gradients
    enable-log-in
  >

    <TransitionTypeformStep :transition-key="currentStepIndex" :fade-transition-key="useBdResume">

      <Component v-if="currentStep"
                 :is="currentStep?.component"
                 :key="currentStepIndex"
                 :primaryForeground="store.applicationForm.primaryForeground"
                 :primaryBackground="store.applicationForm.primaryBackground"
                 :isLastStep="currentStepIndex === steps.length - 1 && steps.length > 1"
                 v-bind="currentStep.props"
                 v-model:validator="validator"
                 v-model:validating="validating"
                 v-model:showLogIn="showLogIn"
                 @toggleUseBdResume="toggleUseBdResume"
                 @restartSteps="restartSteps"
                 @prevStep="prevStep"
                 @nextStep="throttleNextStep"
                 @apply="apply"
                 @cancel="cancel"
      />

    </TransitionTypeformStep>

    <TypeformShortcuts v-model:showLogIn="showLogIn"
                       :prevStepDisabled="!currentStepIndex"
                       :nextStepHidden="currentStepIndex === steps.length - 1 "
                       @nextStep="throttleNextStep"
                       @prevStep="prevStep"
                       enable-log-in/>

  </TypeformContainer>
</template>

<script setup>
import TypeformContainer from "./TypeformContainer.vue";
import TransitionTypeformStep from "./TransitionTypeformStep.vue";
import TypeformShortcuts from "./TypeformShortcuts.vue";
import QuickApplyModalInfo from "./QuickApplyModalInfo.vue";
import QuickApplyModalParseResume from "./QuickApplyModalParseResume.vue";
import QuickApplyModalResumeUpload from "./QuickApplyModalResumeUpload.vue";
import QuickApplyModalScreeningQuestions from "./QuickApplyModalScreeningQuestions.vue";
import QuickApplyModalPhoneVerification from "./QuickApplyModalPhoneVerification.vue";
import QuickApplyModalCompleteProfile from "./QuickApplyModalCompleteProfile.vue";
import QuickApplyModalCreateAccount from "./QuickApplyModalCreateAccount.vue";
import QuickApplyModalNotVerified from "./QuickApplyModalNotVerified.vue";
import {computed, onBeforeMount, onBeforeUnmount, provide, ref, watch} from "vue";
import {useAccountStore} from "../stores/AccountStore.js";
import {useCandidateApplicationFormStore} from "../stores/CandidateApplicationFormStore.js";
import {useCandidateProfileStore} from "../stores/CandidateProfileStore.js";
import {throttle} from "lodash";
import {ABANDONED_SIGN_UP_MODEL_USER, useAbandonedSignUp} from "../composables/AbandonedSignUp";
import {useParseResumeStore} from "../stores/ParseResumeStore";

const emits = defineEmits(["apply", "cancel"]);
const props = defineProps({
  uuid: String,
  primaryForeground: String,
  primaryBackground: String,
  secondaryForeground: String,
  secondaryBackground: String,
  avatar: String,
  logo: String,
  companyName: String,
});

const MIN_TRUST_SCORE_REQUIRED_TO_NOT_NEED_RESUME = 95;

const store = useCandidateApplicationFormStore();
const accountStore = useAccountStore();
const candidateProfileStore = useCandidateProfileStore();
const parseResumeStore = useParseResumeStore();

const validating = ref(false);
const validator = ref(null);

const currentStepIndex = ref(0);

const showLogIn = ref(false);
const useBdResume = ref(false);
const justLoggedIn = ref(false);

const toggleUseBdResume = () => useBdResume.value = !useBdResume.value;

const showCreateUserSteps = computed(() => {
  // They aren't signed in, or it's currently creating them an account
  return !accountStore.isLoggedIn || (store.applicationForm.submitting && !store.applicationForm.candidate);
});

const shouldAskForResume = computed(() => {
  if (showCreateUserSteps.value) return true;
  if (store.applicationForm.candidate?.trustScore && store.applicationForm.candidate?.trustScore >= MIN_TRUST_SCORE_REQUIRED_TO_NOT_NEED_RESUME) return false;
  return !store.applicationForm.candidate?.hasResume;
});

const handleLoggedIn = () => {
  toast.success("You've been logged in! 🎉 Your progress was saved, but the steps required may have changed.");
};

watch(() => accountStore.isLoggedIn, (isLoggedIn, wasLoggedIn) => {
  if (isLoggedIn && !wasLoggedIn) {
    const hasResumeFile = !!parseResumeStore.resume.file;
    const resumeIsRequired = shouldAskForResume.value;

    if (resumeIsRequired && hasResumeFile) currentStepIndex.value = 1;
    else currentStepIndex.value = 0;

    justLoggedIn.value = true;
  }
}, {immediate: true});

const steps = computed(() => {
  if (store.applicationForm.loading) return [];

  let list = [];

  if (store.applicationForm.verified === false) {
    list.unshift({
      component: QuickApplyModalNotVerified,
    });

    return list;
  }

  if (showCreateUserSteps.value) {
    list.unshift({
      component: QuickApplyModalPhoneVerification,
    });

    list.unshift({
      component: QuickApplyModalCreateAccount,
    });
  }

  const askForResume = (shouldAskForResume.value || showCreateUserSteps.value);

  if (askForResume) {
    if (useBdResume.value) {
      list.unshift({
        component: QuickApplyModalCompleteProfile,
      });
    } else {
      list.unshift({
        component: QuickApplyModalResumeUpload,
      });

      list.push({
        component: QuickApplyModalParseResume,
      });
    }
  } else if (store.showCandidateCompleteProfile) {
    list.unshift({
      component: QuickApplyModalCompleteProfile,
      props: {
        hideResume: true,
      }
    });
  }

  if (store.applicationForm.screeningQuestions?.length) {

    const maxQuestionsPerPage = 10;

    const screeningQuestionsGrouped = store.applicationForm.screeningQuestions.reduce((acc, question, index) => {
      const groupIndex = Math.floor(index / maxQuestionsPerPage);
      if (!acc[groupIndex]) acc[groupIndex] = [];
      acc[groupIndex].push(question);
      return acc;
    }, []);

    screeningQuestionsGrouped.forEach((questions, index) => {
      let totalCopy = "";

      if (screeningQuestionsGrouped.length > 1) {
        totalCopy = `Page ${index + 1} of ${screeningQuestionsGrouped.length}`;
      }

      list.push({
        component: QuickApplyModalScreeningQuestions,
        props: {
          questions,
          offset: maxQuestionsPerPage * index,
          totalCopy,
          totalQuestions: store.applicationForm.screeningQuestions.length,
        }
      });
    });
  }

  list.unshift({
    component: QuickApplyModalInfo,
    props: {
      stepCount: list.length,
      estimatedCompletionTime: store.applicationForm.estimatedCompletionTime,
    }
  });

  return list;
});

const currentStep = computed(() => {
  if (currentStepIndex.value >= steps.value.length) return steps.value[steps.value.length - 1];
  return steps.value[currentStepIndex.value];
});

const isLastStep = computed(() => currentStepIndex.value === steps.value.length - 1);
provide("typeformIsLastStep", isLastStep);

const nextStep = async () => {
  if (currentStepIndex.value === steps.value.length - 1) return apply();
  if (validating.value) return;
  validating.value = true;
  if (validator.value && !await validator.value()) return validating.value = false;
  currentStepIndex.value++;
  validator.value = null;
  validating.value = false;
};

const throttleNextStep = throttle(nextStep, 300);

const restartSteps = () => {
  const stepCount = steps.value.length;
  currentStepIndex.value = stepCount > 1 ? 1 : 0;
};

const prevStep = () => {
  if (currentStepIndex.value === 0) return;
  validator.value = null;
  currentStepIndex.value--;
  validating.value = false;
};

const apply = async () => {
  const success = await store.handleSubmit();
  if (!success) return restartSteps();
  emits("apply", true);
};

const cancel = () => emits("cancel");

const setInitialStyles = () => {
  const getStyle = (propName, metaName, defaultValue) => {
    if (props[propName]) return props[propName];
    const headContent = document.head.querySelector(`meta[name="${metaName}"]`)?.content;
    if (headContent) return headContent;
    return defaultValue;
  };

  store.applicationForm.primaryForeground = getStyle("primaryForeground", "primary-foreground", "#FFFFFF");
  store.applicationForm.primaryBackground = getStyle("primaryBackground", "primary-background", "#2C5DB6");
  store.applicationForm.secondaryForeground = getStyle("secondaryForeground", "secondary-foreground", "#FFFFFF");
  store.applicationForm.secondaryBackground = getStyle("secondaryBackground", "secondary-background", "#071A26");
  store.applicationForm.companyLogo = getStyle("logo", "company-logo", "");
  store.applicationForm.companyName = getStyle("companyName", "company-name", "");
  store.applicationForm.companyAvatar = getStyle("avatar", "company-avatar", "");
};

const handleLogInUpdate = (logInOpen) => {
  if (logInOpen || !accountStore.isLoggedIn) return;
  currentStepIndex.value = 1;
};

const getUuid = () => {
  if (props.uuid) return props.uuid;

  try {
    provide("hasRouter", false);
    return window.location.pathname.split("/")[2];
  } catch (e) {
    console.error(e);
    toast.error("Unable to load application form.");
    return "";
  }
};

const {
  manuallyTrigger,
  manuallyStart,
  manuallyCancel,
} = useAbandonedSignUp(ABANDONED_SIGN_UP_MODEL_USER, useCandidateApplicationFormStore, {
  shouldContinueReporting: async () => {
    if (!accountStore.isLoggedIn || !accountStore.user?.created_at) return true;

    const userCreated = new Date(accountStore.user.created_at);
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000); // 60 minutes * 60 seconds * 1000 milliseconds

    return userCreated > oneHourAgo;
  },
  parseResumeStore,
  candidateProfileStore,
});

provide("manuallyTriggerAbandonedSignUp", manuallyTrigger);
provide("manuallyStartAbandonedSignUp", manuallyStart);
provide("manuallyCancelAbandonedSignUp", manuallyCancel);

const load = () => {
  setInitialStyles();
  store.applicationForm.loading = true;
  store.getApplicationForm(getUuid());
  candidateProfileStore.show();
};

onBeforeMount(() => load());
onBeforeUnmount(() => manuallyCancel());
</script>
